import React, { MouseEvent, PropsWithChildren } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DocContentLink } from '../../styles';
import { createDocumentationLocationState } from '../../utils/documentationNavigationState';
import { resolveDocumentationLink } from '../../utils/resolveDocumentationLink';
import { scrollToDocumentationHeading } from '../../utils/scrollToDocumentationHeading';
import {
  DocumentationFeatureLink,
  DocumentationFeatureRouterLink,
} from './htmlDocStyles';

interface DocumentationHtmlLinkProps {
  href?: string;
  variant?: 'body' | 'feature';
}

export function DocumentationHtmlLink({
  href,
  variant = 'body',
  children,
}: PropsWithChildren<DocumentationHtmlLinkProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedLink = resolveDocumentationLink({
    href: href ?? '',
  });
  const isFeatureLink = variant === 'feature';

  function handleHashClick(event: MouseEvent<HTMLAnchorElement>) {
    if (resolvedLink.kind !== 'hash') return;

    event.preventDefault();
    navigate(`${location.pathname}${location.search}#${resolvedLink.hash}`);
    scrollToDocumentationHeading(resolvedLink.hash);
  }

  if (resolvedLink.kind === 'hash') {
    const headingId = resolvedLink.hash;

    const HashLink = isFeatureLink ? DocumentationFeatureLink : 'a';

    return (
      <HashLink href={`#${headingId}`} onClick={handleHashClick}>
        {children}
      </HashLink>
    );
  }

  if (resolvedLink.kind === 'internal') {
    const documentationLocationState =
      createDocumentationLocationState(location);

    if (isFeatureLink) {
      return (
        <DocumentationFeatureRouterLink
          to={resolvedLink.path}
          state={documentationLocationState}
        >
          {children}
        </DocumentationFeatureRouterLink>
      );
    }

    return (
      <DocContentLink to={resolvedLink.path} state={documentationLocationState}>
        {children}
      </DocContentLink>
    );
  }

  if (resolvedLink.kind === 'external') {
    const ExternalLink = isFeatureLink ? DocumentationFeatureLink : 'a';

    return (
      <ExternalLink
        href={resolvedLink.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </ExternalLink>
    );
  }

  const FallbackLink = isFeatureLink ? DocumentationFeatureLink : 'a';

  return <FallbackLink href={href}>{children}</FallbackLink>;
}
